import { memo, type ReactElement, useCallback, useMemo } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';

export type WorkspaceSidebarLibrariesListItemContextMenuProps = {
  children: ReactElement;
  lockToken: string;
  onOpenChange: (open: boolean) => void;
  options: DropdownMenuOption[];
  title: string;
};

export const WorkspaceSidebarLibrariesListItemContextMenu = memo(
  ({ children, lockToken, onOpenChange, options, title }: WorkspaceSidebarLibrariesListItemContextMenuProps) => {
    const { trackSidebarInteraction } = useTrackSidebarInteraction();
    const { handleOpenChange: handleLockChange } = useWorkspaceSidebarPushLock({
      token: lockToken,
    });

    const shouldShowMenu = useMemo(() => options.some((option) => option.type !== 'separator'), [options]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        handleLockChange(open);
        onOpenChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'library',
          });
        }
      },
      [handleLockChange, onOpenChange, trackSidebarInteraction],
    );

    if (!shouldShowMenu) {
      return children;
    }

    return (
      <ContextMenu
        aria-label={`${title} actions`}
        onOpenChange={handleOpenChange}
        trigger={<div className="w-full min-w-0">{children}</div>}
      >
        {renderContextMenuItems({ options })}
      </ContextMenu>
    );
  },
);

WorkspaceSidebarLibrariesListItemContextMenu.displayName = 'WorkspaceSidebarLibrariesListItemContextMenu';
